import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getReadyToPickOrders, getCompletedOrders, refreshAllOrderBalances } from '../supabase/services';
import useDeviceDetect from '../hooks/useDeviceDetect';
import useToast from '../hooks/useToast';
import useAnyModalOpen from '../hooks/useAnyModalOpen';
import useBarcodeBuffer from '../hooks/useBarcodeBuffer';
import scanFeedback from '../utils/scanFeedback';
import { isOverdue } from '../utils/format';
import { filterOrders, sortOrders, findNextOrder, findOrderByCode, orderStatusInfo, orderPath } from './orders/pickLogic';
import Toast from './Toast';
import DueDate from './common/DueDate';
import Meter from './common/Meter';
import { RefreshCw, X, AlertTriangle, ArrowRight, CheckCircle2, SearchX } from 'lucide-react';
import './OrderList.css';

// Filters live in the URL (?view=completed&q=…&customer=…) so the back button from an
// order returns to the same filtered list.
const OrderList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const showCompleted = searchParams.get('view') === 'completed';
  const searchTerm = searchParams.get('q') || '';
  const customerFilter = searchParams.get('customer') || '';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Bumped by Retry to resubscribe without reloading the page
  const [reloadKey, setReloadKey] = useState(0);

  const [refreshing, setRefreshing] = useState(false);

  // Stale data warning
  const [showStaleWarning, setShowStaleWarning] = useState(false);

  const { showToast, toastProps } = useToast();
  const anyModalOpen = useAnyModalOpen();

  // Get device information
  const { isMobile, isTablet, isLandscape } = useDeviceDetect();

  const updateParam = useCallback((key, value) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Unique customers for the dropdown
  const customers = useMemo(
    () => [...new Set(orders.map(order => order.customerName || 'Unknown'))].sort(),
    [orders]
  );

  const filteredOrders = useMemo(
    () => sortOrders(filterOrders(orders, { searchTerm, customerFilter }), showCompleted),
    [orders, searchTerm, customerFilter, showCompleted]
  );

  const nextOrder = useMemo(
    () => (showCompleted ? null : findNextOrder(filteredOrders)),
    [filteredOrders, showCompleted]
  );

  useEffect(() => {
    // Switching view: drop the other view's rows so they never flash under the new heading
    setLoading(true);
    setOrders([]);
    setLoadError(null);
    setShowStaleWarning(false);

    const checkStaleness = (ordersList) => {
      if (ordersList.length > 0) {
        const mostRecent = ordersList.reduce((latest, order) => {
          const t = order.updatedTime?.seconds ? order.updatedTime.seconds * 1000 : 0;
          return t > latest ? t : latest;
        }, 0);
        setShowStaleWarning(mostRecent > 0 && (Date.now() - mostRecent) > 30 * 60 * 1000);
      }
    };

    const handleOrders = (ordersList, error) => {
      setLoading(false);
      if (error) {
        // Keep any previously loaded orders rather than wiping the list on a transient error.
        setLoadError('Could not load orders. Check your connection and retry.');
        return;
      }
      setLoadError(null);
      setOrders(ordersList);
    };

    // Subscribe to real-time updates on orders based on filter
    const unsubscribe = showCompleted
      ? getCompletedOrders(handleOrders)
      : getReadyToPickOrders((ordersList, error) => {
          handleOrders(ordersList, error);
          if (!error) checkStaleness(ordersList);
        });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [showCompleted, reloadKey]);

  // One-off message handed over by the detail view (e.g. after deleting an order)
  useEffect(() => {
    const flash = location.state?.flash;
    if (!flash) return;
    showToast(flash.message, flash.type);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate, showToast]);

  const openOrder = useCallback((order) => {
    navigate(orderPath(order.id), {
      state: { order, from: `${location.pathname}${location.search}` },
    });
  }, [navigate, location.pathname, location.search]);

  // Scanning an order number (e.g. from the pick sheet) opens that order
  const handleOrderScan = useCallback((code) => {
    const order = findOrderByCode(orders, code);
    if (order) {
      scanFeedback('success');
      openOrder(order);
      return;
    }
    scanFeedback('error');
    showToast(`Order ${code.trim()} is not in this list`, 'error');
  }, [orders, openOrder, showToast]);

  useBarcodeBuffer(handleOrderScan, { active: !loading && !anyModalOpen });

  const clearFilters = () => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.delete('q');
      next.delete('customer');
      return next;
    }, { replace: true });
  };

  // Refresh warehouse balance for all visible orders
  const handleRefreshBalance = async () => {
    if (refreshing) return;
    setRefreshing(true);

    const orderIds = orders.map(o => o.id);
    try {
      const result = await refreshAllOrderBalances(orderIds);
      if (result.success) {
        showToast(`Refreshed ${result.updated} orders (${result.totalItems} lines updated)`, 'success');
      } else {
        showToast(`Refreshed ${result.updated} orders, ${result.failed} failed`, 'warning');
      }
    } catch (error) {
      showToast(`Could not refresh balances for ${orderIds.length} orders`, 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const isOrderOverdue = (order) =>
    isOverdue(order.dispatchDate || order.createTime) && order.status !== 'Picked Complete';
  const overdueCount = filteredOrders.filter(isOrderOverdue).length;
  const hasFilters = Boolean(searchTerm || customerFilter);

  // Get responsive filter layout class
  const getFilterLayoutClass = () => {
    return (isMobile || (isTablet && !isLandscape))
      ? 'filters-container mobile-layout'
      : 'filters-container';
  };

  const renderEmpty = () => {
    if (orders.length > 0 && hasFilters) {
      return (
        <div className="no-orders">
          <SearchX size={32} aria-hidden="true" />
          <p>No orders match your filters.</p>
          <button type="button" className="clear-filters-button" onClick={clearFilters}>
            <X size={16} aria-hidden="true" /> Clear filters
          </button>
        </div>
      );
    }
    return (
      <div className="no-orders">
        <CheckCircle2 size={32} aria-hidden="true" />
        <p>{showCompleted ? 'No completed orders yet.' : 'All caught up — no orders to pick.'}</p>
      </div>
    );
  };

  return (
    <div className="order-list-container">
      <Toast {...toastProps} />

      <div className="order-list-header">
        <div className="order-list-title">
          <div className="order-list-heading">
            <h2>{showCompleted ? 'Completed Orders' : 'Ready to Pick Orders'}</h2>
            {!loading && (
              <p className="order-list-count">
                {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''}
                {!showCompleted && overdueCount > 0 && (
                  <span className="order-list-overdue">, {overdueCount} overdue</span>
                )}
              </p>
            )}
          </div>
          <div className="header-buttons">
            {!showCompleted && (
              <button
                type="button"
                className="refresh-balance-button"
                onClick={handleRefreshBalance}
                disabled={refreshing || orders.length === 0}
              >
                <RefreshCw size={16} aria-hidden="true" className={refreshing ? 'spin' : ''} />
                {refreshing ? 'Refreshing…' : 'Refresh Balance'}
              </button>
            )}
            <button
              type="button"
              className={`toggle-view-button ${showCompleted ? 'view-active' : ''}`}
              onClick={() => updateParam('view', showCompleted ? '' : 'completed')}
            >
              {showCompleted ? 'View Ready Orders' : 'View Completed'}
            </button>
          </div>
        </div>

        {!showCompleted && !loading && nextOrder && (
          <button type="button" className="next-order-button" onClick={() => openOrder(nextOrder)}>
            <span className="next-order-label">Next order</span>
            <span className="next-order-id">{nextOrder.id}</span>
            <span className="next-order-customer">{nextOrder.customerName || 'Unknown'}</span>
            <ArrowRight size={20} aria-hidden="true" />
          </button>
        )}

        {showStaleWarning && !showCompleted && (
          <div className="stale-warning-banner" role="status">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>Data may be outdated. We recommend refreshing to ensure all orders are up to date.</span>
            <button type="button" className="stale-warning-dismiss-btn" onClick={() => setShowStaleWarning(false)}>
              <X size={16} aria-hidden="true" /> Dismiss
            </button>
          </div>
        )}

        {loadError && (
          <div className="stale-warning-banner" role="alert">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>{loadError}</span>
            <button type="button" className="stale-warning-dismiss-btn" onClick={() => setReloadKey(k => k + 1)}>
              <RefreshCw size={16} aria-hidden="true" /> Retry
            </button>
          </div>
        )}

        <div className={getFilterLayoutClass()}>
          <div className="search-container">
            <label htmlFor="order-search" className="sr-only">Search orders</label>
            <input
              id="order-search"
              type="search"
              placeholder="Search by order ID or customer..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => updateParam('q', e.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="customer-filter">
            <label htmlFor="order-customer" className="sr-only">Filter by customer</label>
            <select
              id="order-customer"
              className="customer-select"
              value={customerFilter}
              onChange={(e) => updateParam('customer', e.target.value)}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer} value={customer}>
                  {customer}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="clear-filters-button"
            onClick={clearFilters}
            disabled={!hasFilters}
          >
            <X size={16} aria-hidden="true" /> Clear
          </button>
        </div>
        <p className="order-scan-hint">Tip: scan an order number to open it.</p>
      </div>

      {loading ? (
        <div className="skeleton-table" aria-busy="true" aria-label="Loading orders">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton-table-row">
              <div className="skeleton-table-cell skeleton" />
              <div className="skeleton-table-cell skeleton" />
              <div className="skeleton-table-cell skeleton" />
              <div className="skeleton-table-cell skeleton" />
            </div>
          ))}
        </div>
      ) : filteredOrders.length === 0 ? (
        renderEmpty()
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Dispatch</th>
                <th className="status-column">Status</th>
                <th>Lines picked</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => {
                const overdue = isOrderOverdue(order);
                const total = order.totalItems || 0;
                const picked = Math.min(order.pickedCount || 0, total);
                const status = orderStatusInfo(order);

                return (
                  <tr
                    key={order.id}
                    className={`order-row ${overdue ? 'overdue-order' : ''}`}
                    onClick={() => openOrder(order)}
                  >
                    <td className="order-id-cell">
                      {/* The link gives keyboard and screen-reader users a real target;
                          in the phone card layout it stretches over the whole card. */}
                      <Link
                        to={orderPath(order.id)}
                        state={{ order, from: `${location.pathname}${location.search}` }}
                        className="order-link"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Order ${order.id}, ${order.customerName || 'Unknown customer'}, ${status.label}${overdue ? ', overdue' : ''}`}
                      >
                        {order.id}
                      </Link>
                    </td>
                    <td className="order-customer-cell">{order.customerName || 'Unknown'}</td>
                    <td className="order-due-cell">
                      <DueDate
                        date={order.dispatchDate || order.createTime}
                        showRelative={order.status !== 'Picked Complete'}
                      />
                    </td>
                    <td className="status-column">
                      <span className={`order-status ${status.className}`}>{status.label}</span>
                    </td>
                    <td className="picked-cell">
                      <div className="order-pick-progress" title={`${picked} of ${total} lines picked`}>
                        <span className="order-pick-numbers">
                          <strong>{picked}</strong> / {total} lines
                        </span>
                        <Meter value={picked} max={total} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrderList;
